import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { Centrifuge, disconnectedCodes } from 'centrifuge'
import { useLocation } from 'react-router-dom'
import { useStore } from '@store/StoreProvider'
import { handleUnauthorized, handleCentrifugeForcedDisconnect, EmailNotVerifiedError, applyEmailNotVerifiedRestriction } from '@utils/apiHelpers'
import {
  getCentrifugeConnectionCredentials,
  getDialogSubscriptionToken,
  ticketFromCredentials,
  centrifugoJwtFromCredentials,
  centrifugeChannelFromGetTokenArg,
  type CentrifugeConnectCredentials,
} from '@utils/centrifugeTokens'
import { fetchWidgetIdsForCentrifuge } from '@utils/widgetIdsForCentrifuge'
import { logCentrifugeConnectionSubs } from '@utils/centrifugeJwtSubs'
import { startIncomingTransferPolling } from '@utils/incomingTransferPoll'
import { parseOperatorOwnPresenceWsPayload } from '@utils/operatorPresence'
import {
  isOperatorVisitorsFeedChannel,
  parseVisitorsFeedEvent,
} from '@utils/visitorsActiveNow'
import {
  publishVisitorsFeedToOtherTabs,
  subscribeVisitorsFeedFromOtherTabs,
} from '@utils/visitorsRealtimeSync'
import { fetchVisitorsReport } from '@/api/reportsApi'
import { resolveLocalPresetRange } from '@utils/dateTime'
import { isOperatorSettingsUpdatedEvent } from '@/api/workspaceSettingsApi'
import { store } from '@store/Store'
import { applyOperatorTransferNotification } from '@utils/operatorTransferNotifications'
import {
  shouldMarkCentrifugeConnectionProblem,
} from '@utils/operatorCentrifugeConnectionBanner'
import {
  clearSharedDialogChannelSubscriptions,
  isUnacceptedSharedDialog,
  setSharedDialogChannelContext,
  setSharedDialogSoundBuckets,
  forwardDialogChannelToUi,
} from '@utils/operatorDialogChannelSubscriptions'
import {
  getDialogsInboxHubListener,
  getDialogsFeedHubListener,
  doesDialogsPageOwnNotifications,
  notifyFromDialogChannelEvent,
  notifyFromInboxEvent,
  notifyTransferListEffects,
  setDialogsInboxHubListener,
  setDialogsFeedHubListener,
  setDialogsPageOwnsNotifications,
} from '@utils/operatorInboxNotifier'
import { unwrapCentrifugePublication } from '@utils/unwrapCentrifugePublication'
import { toastStore } from '@store/ToastStore'
import { formatNoticeDate, parseBillingWsType } from '@utils/subscriptionNotice'
import { t } from '@store/I18nStore'
const OPERATOR_HEARTBEAT_INTERVAL_MS = 30_000
const OPERATOR_HEARTBEAT_MIN_GAP_MS = 2_000
const OPERATOR_HEARTBEAT_STOP_GRACE_MS = 1_500
const VISITORS_ACTIVE_REST_POLL_MS = 15_000
const VISITORS_WS_FRESH_MS = 8_000

type HeartbeatConfig = {
  baseUrl: string
  headers: Record<string, string>
}

let heartbeatIntervalId: number | null = null
let heartbeatStopTimeoutId: number | null = null
let heartbeatConsumers = 0
let heartbeatLastSentAt = 0
let heartbeatConfig: HeartbeatConfig | null = null

async function sendOperatorHeartbeat(): Promise<void> {
  const cfg = heartbeatConfig
  if (!cfg) return
  const res = await fetch(`${cfg.baseUrl}/api/operator/heartbeat`, {
    method: 'POST',
    headers: cfg.headers,
    body: '{}',
  }).catch(() => null)
  if (!res) return
  if (res.status === 429) {
    heartbeatLastSentAt = Date.now() + OPERATOR_HEARTBEAT_INTERVAL_MS
    return
  }
  handleUnauthorized(res)
}

function sendHeartbeatIfDue(): void {
  const now = Date.now()
  if (now - heartbeatLastSentAt < OPERATOR_HEARTBEAT_MIN_GAP_MS) {
    return
  }
  heartbeatLastSentAt = now
  void sendOperatorHeartbeat()
}

function ensureHeartbeatLoop(): void {
  if (heartbeatStopTimeoutId !== null) {
    window.clearTimeout(heartbeatStopTimeoutId)
    heartbeatStopTimeoutId = null
  }
  if (heartbeatIntervalId !== null) return
  sendHeartbeatIfDue()
  heartbeatIntervalId = window.setInterval(() => {
    sendHeartbeatIfDue()
  }, OPERATOR_HEARTBEAT_INTERVAL_MS)
}

function stopHeartbeatLoopIfUnused(): void {
  if (heartbeatConsumers > 0) return
  if (heartbeatStopTimeoutId !== null) return
  heartbeatStopTimeoutId = window.setTimeout(() => {
    heartbeatStopTimeoutId = null
    if (heartbeatConsumers > 0) return
    if (heartbeatIntervalId !== null) {
      window.clearInterval(heartbeatIntervalId)
      heartbeatIntervalId = null
    }
    heartbeatConfig = null
  }, OPERATOR_HEARTBEAT_STOP_GRACE_MS)
}

type CountsTriple = { inbox: number; my: number; archive: number }
type WsCountsByWidget = Record<string, CountsTriple>

function safeJsonParse(s: string): any {
  try {
    return JSON.parse(s)
  } catch {
    return null
  }
}

function clamp(v: unknown): number {
  return Math.max(0, Math.trunc(Number(v) || 0))
}

function extractWidgetId(channel: string): string {
  const m = channel.match(/^widget\.(\w+)\./)
  return m?.[1] ?? ''
}

function asConnectRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

type RealtimeConnectAccess = {
  canUseWorkspace: boolean | null
  systemOnly: boolean
}

/** Parse connect-reply data.permissions / mode, including system_only. */
function parseRealtimeConnectAccess(data: unknown): RealtimeConnectAccess {
  const root = asConnectRecord(data)
  if (!root) return { canUseWorkspace: null, systemOnly: false }

  const permissions = asConnectRecord(root.permissions) ?? asConnectRecord(root.permission)
  const access = asConnectRecord(root.access)
  const nestedData = asConnectRecord(root.data)
  const modeRaw = [
    root.mode,
    root.accessMode,
    permissions?.mode,
    access?.state,
    access?.mode,
    nestedData?.mode,
  ]
  const mode =
    modeRaw
      .map((value) => String(value ?? '').trim().toLowerCase())
      .find(Boolean) ?? ''

  const systemOnly =
    mode === 'system_only' ||
    mode === 'system-only' ||
    root.systemOnly === true ||
    permissions?.systemOnly === true

  let canUseWorkspace: boolean | null = null
  if (typeof permissions?.canUseWorkspace === 'boolean') {
    canUseWorkspace = permissions.canUseWorkspace
  } else if (typeof root.canUseWorkspace === 'boolean') {
    canUseWorkspace = root.canUseWorkspace
  }
  if (systemOnly) canUseWorkspace = false

  return { canUseWorkspace, systemOnly }
}

/** SDK emits `disconnected` only when it will not auto-reconnect. */
function shouldManuallyReconnectCentrifuge(code: number | undefined): boolean {
  if (code === disconnectedCodes.disconnectCalled) return false
  if (code === disconnectedCodes.unauthorized) return false
  if (code === disconnectedCodes.badProtocol) return false
  if (code === disconnectedCodes.messageSizeLimit) return false
  // 3501 "bad request" / other 35xx: credentials or protocol, not a blip — do not hammer.
  if (code != null && code >= 3500 && code < 4000) return false
  return true
}

type CentrifugeContextValue = {
  centrifuge: any
  isReady: boolean
  widgetIds: string[]
  workspaceEnabled: boolean
  /** Resolves with the live instance after a full re-init (or the current one on timeout). */
  refreshConnection: () => Promise<any>
}

const CentrifugeContext = createContext<CentrifugeContextValue>({
  centrifuge: null,
  isReady: false,
  widgetIds: [],
  workspaceEnabled: true,
  refreshConnection: async () => null,
})

export function useCentrifuge() {
  return useContext(CentrifugeContext)
}

export const DialogsCountersProvider: React.FC<{ children: React.ReactNode }> = observer(({ children }) => {
  const { auth, i18n, ui, billing, emailVerification } = useStore()
  const location = useLocation()
  const authHeaders = useMemo((): Record<string, string> => {
    if (!auth.accessToken) return {}
    return { Authorization: `Bearer ${auth.accessToken}` }
  }, [auth.accessToken])
  /**
   * Live headers for the long-lived realtime client. The access token is silently refreshed
   * every ~30 min; that must NOT rebuild the connection, so the connect effect reads the
   * current Bearer through this ref instead of depending on `auth.accessToken`.
   */
  const authHeadersRef = useRef(authHeaders)
  authHeadersRef.current = authHeaders
  /** Consecutive 3500 "unauthorized" disconnects answered with a fresh-ticket reconnect. */
  const unauthorizedReconnectsRef = useRef(0)
  const [centrifuge, setCentrifuge] = useState<any>(null)
  const [isReady, setIsReady] = useState(false)
  const [widgetIds, setWidgetIds] = useState<string[]>([])
  // Bumped when the credential refresh has to swap in a fresh Centrifuge
  // instance (ticket rotated, mode flipped). We never mutate a live client
  // to avoid sending both `data.ticket` and `connect.token` on the wire.
  const [reinitEpoch, setReinitEpoch] = useState(0)
  const countsByWidgetRef = useRef<WsCountsByWidget>({})
  const centrifugeRef = useRef<any>(null)
  /** True while refreshConnection is swapping the Centrifuge instance. */
  const reinitPendingRef = useRef(false)
  const refreshWaitersRef = useRef<Array<{ resolve: (instance: any) => void; timer: number }>>([])

  const settleRefreshWaiters = useCallback((instance: any) => {
    const waiters = refreshWaitersRef.current
    refreshWaitersRef.current = []
    for (const waiter of waiters) {
      window.clearTimeout(waiter.timer)
      waiter.resolve(instance)
    }
  }, [])
  const applyToUiRef = useRef<(totals: CountsTriple) => void>(() => {})
  const suppressCountersDisconnectBannerRef = useRef(false)
  const presenceSubRef = useRef<{ unsubscribe: () => void; removeAllListeners: () => void } | null>(null)
  const operatorIdRef = useRef<number | null>(null)
  const visitorsWsAtRef = useRef(0)
  const applyVisitorsFromWsRef = useRef<(raw: unknown, broadcast?: boolean) => boolean>(() => false)
  applyVisitorsFromWsRef.current = (raw: unknown, broadcast = true) => {
    const ev = parseVisitorsFeedEvent(raw)
    if (!ev) return false
    visitorsWsAtRef.current = Date.now()
    runInAction(() => ui.applyVisitorsFeedEvent(ev))
    if (broadcast) publishVisitorsFeedToOtherTabs(ev)
    return true
  }

  // Hard guarantee: off /dialogs the page must not "own" notifications. Ownership can
  // otherwise stick true after an async mount race and silence all global sounds.
  useEffect(() => {
    if (location.pathname.startsWith('/dialogs')) return
    setDialogsPageOwnsNotifications(false)
    setDialogsInboxHubListener(null)
    setDialogsFeedHubListener(null)
  }, [location.pathname])

  useEffect(() => {
    const id = Number(auth.currentUser?.id)
    operatorIdRef.current = Number.isFinite(id) && id > 0 ? id : null
  }, [auth.currentUser?.id])

  const applyToUi = useCallback(
    (totals: CountsTriple) => {
      runInAction(() => {
        ui.setInboxDialogsCount(totals.inbox)
        ui.setMyDialogsCount(totals.my)
        ui.setArchiveDialogsCount(totals.archive)
      })
    },
    [ui],
  )
  applyToUiRef.current = applyToUi

  useEffect(() => {
    if (!auth.isAuthenticated || !i18n.apiBaseUrl || !i18n.wsUrl) return
    if (emailVerification.blocked) return
    if (auth.currentUser?.emailVerified === false) {
      applyEmailNotVerifiedRestriction()
      return
    }

    const baseUrl = (i18n.apiBaseUrl ?? '').replace(/\/$/, '')
    const wsUrl = i18n.wsUrl ?? ''
    const markCountersWsProblem = () => {
      runInAction(() => ui.setWsConnectionIssue('counters', true))
    }

    const handleOffline = () => {
      markCountersWsProblem()
    }

    const handleOnline = () => {
      runInAction(() => ui.clearWsConnectionIssuesOnNetworkOnline())
    }

    const recalcTotals = () => {
      const totals = Object.values(countsByWidgetRef.current).reduce<CountsTriple>(
        (acc, c) => ({
          inbox: acc.inbox + (c?.inbox ?? 0),
          my: acc.my + (c?.my ?? 0),
          archive: acc.archive + (c?.archive ?? 0),
        }),
        { inbox: 0, my: 0, archive: 0 },
      )
      applyToUiRef.current(totals)
    }

    const applyWidgetCounts = (widgetId: string, next: CountsTriple) => {
      countsByWidgetRef.current = { ...countsByWidgetRef.current, [widgetId]: next }
      recalcTotals()
    }

    const parseCounts = (data: any): CountsTriple | null => {
      if (!data || typeof data !== 'object') return null
      if ('inbox' in data && 'my' in data && 'archive' in data) {
        return { inbox: clamp(data.inbox), my: clamp(data.my), archive: clamp(data.archive) }
      }
      if (data.type === 'dialogs.counts' && data.payload) {
        const p = data.payload
        return { inbox: clamp(p.inbox), my: clamp(p.my), archive: clamp(p.archive) }
      }
      return null
    }

    const applyCountsOrPartial = (widgetId: string, raw: any) => {
      if (raw == null || typeof raw !== 'object') return
      const data = typeof raw === 'string' ? safeJsonParse(raw) : raw
      if (!data || typeof data !== 'object') return

      const topLevel = data.data != null && typeof data.data === 'object' ? data.data : data
      const unwrapped =
        data.type === 'counters.updated' && data.data != null && typeof data.data === 'object'
          ? data.data
          : topLevel

      const full = parseCounts(unwrapped) ?? parseCounts(data)
      if (full) {
        applyWidgetCounts(widgetId, full)
        return
      }
      if (!unwrapped || typeof unwrapped !== 'object') return
      if ('inbox' in unwrapped || 'my' in unwrapped || 'archive' in unwrapped) {
        const cur = countsByWidgetRef.current[widgetId]
        let nextInbox = cur?.inbox ?? 0
        if ('inbox' in unwrapped) {
          nextInbox = clamp(unwrapped.inbox)
        }
        const next: CountsTriple = {
          inbox: nextInbox,
          my: 'my' in unwrapped ? clamp(unwrapped.my) : (cur?.my ?? 0),
          archive: 'archive' in unwrapped ? clamp(unwrapped.archive) : (cur?.archive ?? 0),
        }
        countsByWidgetRef.current = { ...countsByWidgetRef.current, [widgetId]: next }
        recalcTotals()
      }
    }

    const applyMyCount = (widgetId: string, raw: any) => {
      const data = typeof raw === 'string' ? safeJsonParse(raw) : raw
      if (data == null) return
      let myCount: number | null = null
      if (typeof data === 'number') myCount = data
      else if (typeof data?.my === 'number') myCount = data.my
      else if (data?.type === 'counters.my.updated' && data?.payload != null) {
        const p = data.payload
        myCount = typeof p.my === 'number' ? p.my : typeof p === 'number' ? p : null
      } else if (data?.payload && typeof data.payload.my === 'number') {
        myCount = data.payload.my
      }
      if (myCount === null) return
      const n = clamp(myCount)
      const cur = countsByWidgetRef.current[widgetId]
      const next: CountsTriple = { inbox: cur?.inbox ?? 0, my: n, archive: cur?.archive ?? 0 }
      countsByWidgetRef.current = { ...countsByWidgetRef.current, [widgetId]: next }
      recalcTotals()
    }

    const handleServerSub = (channel: string, data: any) => {
      const wid = extractWidgetId(channel)
      if (!wid) return
      if (channel.includes('.counters.my.')) {
        applyMyCount(wid, data)
      } else if (channel.endsWith('.counters')) {
        applyCountsOrPartial(wid, data)
      }
    }

    const handleOperatorNotifications = (channel: string, raw: unknown) => {
      if (!channel.includes('.notifications')) return
      const me = operatorIdRef.current
      if (!me) {
        console.warn('[transfer] notifications event without operatorId', channel)
        return
      }
      const data = typeof raw === 'string' ? safeJsonParse(raw) : raw
      if (!data || typeof data !== 'object') return
      runInAction(() => {
        applyOperatorTransferNotification(data, me, store.operatorTransfers)
      })
    }

    /** Apply transfer events from any channel (notifications, dialog.*, feed) — backend routing varies. */
    const tryApplyTransferFromAnyChannel = (channel: string, data: unknown) => {
      if (!data || typeof data !== 'object') return
      const t = String(
        (data as { type?: string; event?: string }).type ??
          (data as { event?: string }).event ??
          '',
      ).toLowerCase()
      if (!t.includes('transfer')) return
      const me = operatorIdRef.current
      if (!me) return
      runInAction(() => {
        applyOperatorTransferNotification(data, me, store.operatorTransfers)
      })
      notifyTransferListEffects(data)
    }

    const handleBillingPublication = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return
      const type = parseBillingWsType(raw)
      if (type === 'subscription.notice') {
        billing.applyNotice(raw)
        return
      }
      if (type === 'subscription.expired') {
        billing.applyExpired({
          code: 'SUBSCRIPTION_EXPIRED',
          reason:
            typeof (raw as { reason?: unknown }).reason === 'string'
              ? (raw as { reason: string }).reason
              : null,
          details: (raw as { details?: unknown }).details ?? raw,
        })
        clearSharedDialogChannelSubscriptions()
        return
      }
      if (type === 'subscription.renewed') {
        void (async () => {
          await billing.handleRenewed(baseUrl, authHeaders, { recreateConnection: true })
          if (!billing.canManageBilling) return
          const end = billing.entitlements?.subscription?.currentPeriodEnd
          toastStore.show(
            end
              ? t('billing.subscription.renewed_until', { date: formatNoticeDate(end) })
              : t('billing.subscription.renewed'),
            'success',
            5000,
          )
        })()
      }
    }

    const routeConnectionEvent = (channel: string, raw: unknown) => {
      const data = unwrapCentrifugePublication(raw)
      tryApplyTransferFromAnyChannel(channel, data)
      if (channel.includes('.notifications')) {
        handleOperatorNotifications(channel, data)
        return
      }
      // Per-dialog server-side subs (recent dialogs embedded in the connection token)
      // deliver here. While the dialogs page is not mounted, fire message notifications
      // globally so operators still get sound/desktop alerts off /dialogs. When it is
      // mounted, DialogsPage handles these events itself via attachDialogsCentrifugeHandlers.
      if (channel.startsWith('dialog.')) {
        // Always forward UI; sounds only when DialogsPage is not owning notifications.
        forwardDialogChannelToUi(channel, data)
        if (!doesDialogsPageOwnNotifications()) {
          const rest = channel.slice('dialog.'.length)
          const dotIdx = rest.indexOf('.')
          const chatId = (dotIdx === -1 ? rest : rest.slice(0, dotIdx)).trim()
          const soundBucket =
            chatId && isUnacceptedSharedDialog(chatId) ? 'unaccepted' : 'accepted'
          notifyFromDialogChannelEvent(channel, data, { soundBucket })
        }
        return
      }
      // widget.<id>.dialogs.inbox — JWT server-side (Berlin TZ); no client newSubscription.
      if (channel.includes('.dialogs.inbox')) {
        const wid = extractWidgetId(channel)
        if (wid) {
          const listener = getDialogsInboxHubListener()
          if (listener) listener(data, wid, 'publication')
          if (!doesDialogsPageOwnNotifications()) notifyFromInboxEvent(data)
        }
        return
      }
      // operator.{id}.dialogs.feed — chat.added_to_my / chat.removed_from_my / message.created
      if (/^operator\.\d+\.dialogs\.feed$/.test(channel)) {
        const feedListener = getDialogsFeedHubListener()
        if (feedListener) feedListener(data)
        return
      }
      // operator.{id}.billing — subscription.notice / expired / renewed
      if (/^operator\.\d+\.billing$/.test(channel)) {
        handleBillingPublication(data)
        return
      }
      // operator.{id}.visitors.feed — must run here (before connect), or the
      // subscribe snapshot is missed: the React effect only attaches after isReady.
      if (isOperatorVisitorsFeedChannel(channel)) {
        applyVisitorsFromWsRef.current(raw) || applyVisitorsFromWsRef.current(data)
        return
      }
      handleServerSub(channel, data)
    }

    let cancelled = false
    let localInst: any = null

    const run = async () => {
      try {
        const operatorId = auth.currentUser?.id
        if (operatorId == null || !Number.isFinite(Number(operatorId)) || Number(operatorId) <= 0) {
          console.warn('[DialogsCountersProvider] Skip Centrifuge init: missing operator id')
          return
        }
        if (cancelled) return

        // Every (re)connect gets its own ticket (backend: connect proxy checks the sid of the
        // current JWT) — never reuse a cached one.
        const creds = await getCentrifugeConnectionCredentials(baseUrl, authHeadersRef.current, { force: true })
        if (cancelled) return
        const connectTicket = ticketFromCredentials(creds)
        const connectionJwt = centrifugoJwtFromCredentials(creds)
        if (!connectTicket && !connectionJwt) {
          console.warn('[DialogsCountersProvider] Skip Centrifuge init: no connect ticket or JWT')
          return
        }
        if (connectionJwt) {
          logCentrifugeConnectionSubs(
            connectionJwt,
            `operator.${operatorId}.notifications`,
          )
        } else {
          logCentrifugeConnectionSubs(
            connectTicket,
            `operator.${operatorId}.notifications`,
          )
        }

        let currentCreds: CentrifugeConnectCredentials = { ...creds }
        let inst: InstanceType<typeof Centrifuge> | null = null
        let manualReconnectInFlight = false
        const useProxyMode = creds.mode === 'proxy'

        /** Before each connect attempt: a fresh ticket with the CURRENT Bearer (in-flight merge only). */
        const credentialsForConnectAttempt = () =>
          getCentrifugeConnectionCredentials(
            (i18n.apiBaseUrl ?? '').replace(/\/$/, '') || baseUrl,
            authHeadersRef.current,
            { force: true },
          )

        const triggerReinit = () => {
          if (cancelled) return
          try {
            inst?.disconnect()
          } catch {
            /* ignore */
          }
          setReinitEpoch((n) => n + 1)
        }

        const reconnectWithFreshCredentials = async () => {
          if (cancelled || manualReconnectInFlight) return
          const target = inst
          if (!target) return
          const state = String((target as { state?: string }).state ?? '')
          if (state === 'connecting' || state === 'connected') return
          manualReconnectInFlight = true
          try {
            const next = await credentialsForConnectAttempt()
            if (cancelled) return
            currentCreds = { ...next }
            // Ticket rotated / mode may have flipped — rebuild the instance so
            // we never mutate a live client to send both `data.ticket` and
            // `connect.token`.
            triggerReinit()
          } catch (err) {
            if (err instanceof EmailNotVerifiedError) return
            console.error('[DialogsCountersProvider] reconnect with fresh credentials failed:', err)
          } finally {
            manualReconnectInFlight = false
          }
        }

        if (useProxyMode) {
          if (!connectTicket) {
            console.warn('[DialogsCountersProvider] proxy mode without a ticket — aborting init')
            return
          }
          // Backend authMode='connect_proxy' → identify with data.ticket only, never a
          // connect.token (the proxy issues it server-side; sending both = hybrid frames).
          // `getData` runs before EVERY connect attempt, the first one included: the first
          // call hands over the ticket fetched above, every reconnect asks the backend for a
          // new one with the current Bearer — a refreshed access token never breaks the WS,
          // and a dead session shows up as 401 on the ticket request (logout happens there).
          let initialTicket: string | null = connectTicket
          inst = new Centrifuge(wsUrl, {
            getData: async () => {
              if (cancelled) throw new Error('cancelled')
              if (initialTicket) {
                const ticket = initialTicket
                initialTicket = null
                return { ticket }
              }
              const next = await credentialsForConnectAttempt()
              if (cancelled) throw new Error('cancelled')
              currentCreds = { ...next }
              const ticket = ticketFromCredentials(next)
              if (!ticket) throw new Error('empty connect ticket')
              return { ticket }
            },
            timeout: 15000,
            minReconnectDelay: 1000,
            maxReconnectDelay: 20000,
            debug: false,
          })
        } else {
          if (!connectionJwt) {
            console.warn('[DialogsCountersProvider] jwt mode without a token — aborting init')
            return
          }
          // Legacy JWT branch — only when backend explicitly returned
          // authMode='jwt'. Send token only; ticket must not appear in data.
          inst = new Centrifuge(wsUrl, {
            token: connectionJwt,
            timeout: 15000,
            minReconnectDelay: 1000,
            maxReconnectDelay: 20000,
            debug: false,
            getToken: async () => {
              if (cancelled) throw new Error('cancelled')
              if (String(inst?.state ?? '') === 'connected') {
                return centrifugoJwtFromCredentials(currentCreds)
              }
              const next = await credentialsForConnectAttempt()
              if (cancelled) throw new Error('cancelled')
              if (next.mode !== 'jwt') {
                console.warn(
                  '[DialogsCountersProvider] backend flipped to proxy mid-session; forcing re-init',
                )
                triggerReinit()
                throw new Error('mode-flipped')
              }
              currentCreds = { ...next }
              return centrifugoJwtFromCredentials(currentCreds)
            },
          })
        }

        if (cancelled) {
          try { inst.disconnect() } catch { /* ignore */ }
          return
        }
        localInst = inst

        inst.on('subscribed', (ctx: any) => {
          const ch: string = ctx.channel ?? ''
          routeConnectionEvent(ch, ctx?.data)
        })

        inst.on('publication', (ctx: any) => {
          const ch: string = ctx.channel ?? ''
          routeConnectionEvent(ch, ctx?.data)
        })

        inst.on('connecting', (ctx: { code?: number; reason?: string }) => {
          if (handleCentrifugeForcedDisconnect(ctx)) return
        })

        inst.on('connected', (ctx: { client?: string; transport?: string; data?: unknown }) => {
          const access = parseRealtimeConnectAccess(ctx?.data)
          if (cancelled) return
          unauthorizedReconnectsRef.current = 0
          settleRefreshWaiters(inst)
          runInAction(() => ui.setWsConnectionIssue('counters', false))

          if (access.systemOnly || access.canUseWorkspace === false) {
            billing.applyExpired({
              code: 'SUBSCRIPTION_EXPIRED',
              details: ctx?.data,
            })
            clearSharedDialogChannelSubscriptions()
            setIsReady(false)
            return
          }

          if (billing.workspaceRestricted) {
            setIsReady(false)
            if (access.canUseWorkspace === true) {
              void billing.handleRenewed(baseUrl, authHeadersRef.current, { recreateConnection: false })
              setIsReady(true)
            }
            return
          }

          setIsReady(true)
        })

        inst.on('disconnected', (ctx: { code?: number; reason?: string }) => {
          if (handleCentrifugeForcedDisconnect(ctx)) return
          if (cancelled) return
          setIsReady(false)
          if (!suppressCountersDisconnectBannerRef.current) {
            markCountersWsProblem()
          }
          if (shouldManuallyReconnectCentrifuge(ctx?.code)) {
            void reconnectWithFreshCredentials()
            return
          }
          // 3500: the ticket was not accepted (ran out between fetch and connect, or the
          // session rotated). One more attempt with a fresh ticket + current JWT, at most
          // twice in a row — a really dead session answers 401 on the ticket request and
          // logs out there, so this cannot loop.
          if (ctx?.code === disconnectedCodes.unauthorized && unauthorizedReconnectsRef.current < 2) {
            unauthorizedReconnectsRef.current += 1
            window.setTimeout(() => {
              if (!cancelled) void reconnectWithFreshCredentials()
            }, 3_000)
          }
        })

        inst.on('error', (ctx: { type?: string; error?: { code?: number; message?: string } }) => {
          if (handleCentrifugeForcedDisconnect(ctx)) return
          if (shouldMarkCentrifugeConnectionProblem(ctx, inst)) {
            markCountersWsProblem()
          }
        })

        centrifugeRef.current = inst
        setCentrifuge(inst)
        setSharedDialogChannelContext({
          centrifuge: inst,
          getToken: async (channel: string) => {
            const base = (i18n.apiBaseUrl ?? '').replace(/\/$/, '')
            const name = centrifugeChannelFromGetTokenArg(channel)
            if (!name) return ''
            return (await getDialogSubscriptionToken(base, authHeadersRef.current, name)) || ''
          },
        })
        inst.connect()
      } catch (err) {
        if (cancelled) {
          settleRefreshWaiters(centrifugeRef.current)
          return
        }
        if (err instanceof EmailNotVerifiedError) {
          console.warn('[DialogsCountersProvider] email not verified — skipping Centrifuge')
          settleRefreshWaiters(null)
          return
        }
        console.error('[DialogsCountersProvider] Init error:', err)
        settleRefreshWaiters(centrifugeRef.current)
      }
    }

    void run()
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      cancelled = true
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      suppressCountersDisconnectBannerRef.current = true
      runInAction(() => ui.setWsConnectionIssue('counters', false))
      if (localInst) {
        try { localInst.disconnect() } catch (e) {
          console.error('[DialogsCountersProvider] Cleanup error:', e)
        }
      }
      if (centrifugeRef.current === localInst) {
        centrifugeRef.current = null
        // Keep React state on the old instance during refresh so DialogsPage
        // does not tear down and resubscribe against a null / dead client.
        if (!reinitPendingRef.current) {
          setSharedDialogChannelContext({ centrifuge: null, getToken: null })
          clearSharedDialogChannelSubscriptions()
          setCentrifuge(null)
          setIsReady(false)
          setWidgetIds([])
          visitorsWsAtRef.current = 0
        }
      }
      suppressCountersDisconnectBannerRef.current = false
    }
    // NOTE: no `auth.accessToken` here on purpose — a token refresh must not rebuild the WS.
  }, [auth.isAuthenticated, auth.currentUser?.id, auth.currentUser?.emailVerified, i18n.apiBaseUrl, i18n.wsUrl, billing.connectionEpoch, emailVerification.blocked, emailVerification.connectionEpoch, reinitEpoch])

  /**
   * Sound classification from REST (inbox vs my). Skip on /dialogs — DialogsPage
   * already classifies from the lists it loaded (avoids a second inbox+my burst).
   */
  useEffect(() => {
    if (!auth.isAuthenticated || !centrifuge || !isReady || !i18n.apiBaseUrl || !billing.canUseWorkspace) return
    if (location.pathname.startsWith('/dialogs')) return

    let cancelled = false
    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')

    const loadIds = async (tab: 'my' | 'inbox'): Promise<string[]> => {
      try {
        const params = new URLSearchParams({ tab, page: '1', perPage: '50' })
        const res = await fetch(`${base}/api/operator/dialogs?${params}`, {
          headers: { ...authHeaders },
        })
        if (!res.ok) return []
        const json: any = await res.json().catch(() => null)
        const rawList = json?.data?.items ?? json?.items ?? json?.data ?? (Array.isArray(json) ? json : [])
        const list = Array.isArray(rawList) ? rawList : []
        return list
          .map((d: any) => String(d?.id ?? d?.chatId ?? d?.chat_id ?? '').trim())
          .filter(Boolean)
      } catch {
        return []
      }
    }

    void (async () => {
      const [myIds, inboxIds] = await Promise.all([loadIds('my'), loadIds('inbox')])
      if (cancelled) return
      if (myIds.length > 0 || inboxIds.length > 0) {
        setSharedDialogSoundBuckets({
          unacceptedIds: inboxIds,
          acceptedIds: myIds,
        })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [auth.isAuthenticated, centrifuge, isReady, i18n.apiBaseUrl, authHeaders, billing.canUseWorkspace, location.pathname])

  useEffect(() => {
    if (!auth.isAuthenticated || !isReady || !billing.canUseWorkspace || !i18n.apiBaseUrl) {
      return
    }
    let cancelled = false
    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    void fetchWidgetIdsForCentrifuge(base, authHeaders).then((result) => {
      if (cancelled || result.unauthorized) return
      setWidgetIds(result.widgetIds)
    })
    return () => {
      cancelled = true
    }
  }, [
    auth.isAuthenticated,
    auth.currentUser?.id,
    isReady,
    billing.canUseWorkspace,
    i18n.apiBaseUrl,
    authHeaders,
  ])

  /** WS operator.<me>.presence — JWT server-side (Berlin TZ); client-level publication only. */
  useEffect(() => {
    if (!auth.isAuthenticated || !centrifuge || !isReady || !auth.currentUser?.id || !billing.canUseWorkspace) {
      return
    }

    const me = auth.currentUser.id
    const channel = `operator.${me}.presence`
    const onPublication = (ctx: { channel?: string; data?: unknown }) => {
      if (ctx?.channel !== channel) return
      const parsed = parseOperatorOwnPresenceWsPayload(ctx?.data, me)
      if (!parsed) return
      runInAction(() => store.operatorPresence.setPresence(parsed))
    }

    centrifuge.on('publication', onPublication)
    presenceSubRef.current = null

    return () => {
      try {
        centrifuge.off?.('publication', onPublication)
      } catch {
        /* ignore */
      }
    }
  }, [auth.isAuthenticated, auth.currentUser?.id, centrifuge, isReady, billing.canUseWorkspace])

  /** Catch subscribe snapshot if it arrived before React attached (JWT server-side). */
  useEffect(() => {
    if (!auth.isAuthenticated || !centrifuge || !isReady || !auth.currentUser?.id || !billing.canUseWorkspace) {
      return
    }

    const me = auth.currentUser.id
    const channel = `operator.${me}.visitors.feed`
    try {
      const sub =
        centrifuge.getServerSubscription?.(channel) ??
        centrifuge.getServerSubscription?.(`operator.${me}.visitors`)
      const ev = parseVisitorsFeedEvent(sub?.data)
      if (ev && (ev.activeNow != null || ev.kind === 'snapshot')) {
        applyVisitorsFromWsRef.current(sub?.data)
      }
    } catch {
      /* ignore */
    }
  }, [auth.isAuthenticated, auth.currentUser?.id, centrifuge, isReady, billing.canUseWorkspace])

  /** REST seed for the sidebar badge — WS snapshots are often empty (no recover). */
  useEffect(() => {
    if (!auth.isAuthenticated || !billing.canUseWorkspace || !i18n.apiBaseUrl) return

    let cancelled = false
    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')

    const seedFromRest = async () => {
      if (Date.now() - visitorsWsAtRef.current < VISITORS_WS_FRESH_MS) return
      const range = resolveLocalPresetRange('today')
      const result = await fetchVisitorsReport(base, range, authHeaders)
      if (cancelled || !result.ok) return
      if (Date.now() - visitorsWsAtRef.current < VISITORS_WS_FRESH_MS) return
      const n = result.data.summary?.activeNow
      if (typeof n !== 'number' || !Number.isFinite(n)) return
      runInAction(() => ui.setVisitorsActiveNow(n))
    }

    void seedFromRest()
    const intervalId = window.setInterval(() => {
      void seedFromRest()
    }, VISITORS_ACTIVE_REST_POLL_MS)

    const onVisible = () => {
      if (document.visibilityState === 'visible') void seedFromRest()
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [auth.isAuthenticated, billing.canUseWorkspace, i18n.apiBaseUrl, authHeaders, ui])

  /** Other operator tabs get the same visitors.feed count/list immediately. */
  useEffect(() => {
    if (!auth.isAuthenticated || !billing.canUseWorkspace) return
    return subscribeVisitorsFeedFromOtherTabs((event) => {
      visitorsWsAtRef.current = Date.now()
      runInAction(() => ui.applyVisitorsFeedEvent(event))
    })
  }, [auth.isAuthenticated, billing.canUseWorkspace, ui])

  /**
   * WS operator.<me>.notifications — JWT `subs` only (Berlin TZ).
   * Do NOT newSubscription() — that yields Centrifugo 103 after the token migration.
   * Transfer events arrive via centrifuge.on('publication').
   * In system/restricted mode missing notifications must NOT start REST transfer polling.
   */
  useEffect(() => {
    if (!auth.isAuthenticated || !centrifuge || !isReady || !auth.currentUser?.id || !i18n.apiBaseUrl || !billing.canUseWorkspace) {
      return
    }

    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    const me = Number(auth.currentUser.id)
    if (!Number.isFinite(me) || me <= 0) return
    const channel = `operator.${me}.notifications`
    let cancelled = false
    let stopPoll: (() => void) | null = null
    let watchdogId = 0

    const isServerSide = (): boolean => {
      try {
        if (centrifuge.getServerSubscription?.(channel)) return true
      } catch {
        /* ignore */
      }
      try {
        const serverSubs = (centrifuge as { _serverSubs?: Record<string, unknown> })._serverSubs
        return serverSubs?.[channel] != null
      } catch {
        return false
      }
    }

    const onPublication = (ctx: { channel?: string; data?: unknown }) => {
      if (ctx?.channel !== channel) return
      const data = unwrapCentrifugePublication(ctx?.data)
      if (!data || typeof data !== 'object') return
      runInAction(() => {
        applyOperatorTransferNotification(data, me, store.operatorTransfers)
      })
    }
    centrifuge.on('publication', onPublication)

    const waitUntilConnected = async () => {
      const state = String((centrifuge as { state?: string }).state ?? '')
      if (state === 'connected') return
      await new Promise<void>((resolve) => {
        const done = () => {
          try {
            centrifuge.off?.('connected', done)
          } catch {
            /* ignore */
          }
          resolve()
        }
        centrifuge.on('connected', done)
        window.setTimeout(done, 8000)
      })
    }

    void (async () => {
      await waitUntilConnected()
      if (cancelled) return
      const granted = isServerSide()
      if (granted) {
        return
      }
      console.error(
        '[transfer] BACKEND: connection ticket missing',
        channel,
        '— POST /api/realtime/connect-ticket must embed it in `subs`. Starting REST pending poll.',
      )
      stopPoll = startIncomingTransferPolling(base, authHeaders, me)
      watchdogId = window.setTimeout(() => {
        if (cancelled || isServerSide()) return
        console.error('[transfer] notifications still absent from JWT after 8s', channel)
      }, 8000)
    })()

    return () => {
      cancelled = true
      if (watchdogId) window.clearTimeout(watchdogId)
      stopPoll?.()
      try {
        centrifuge.off?.('publication', onPublication)
      } catch {
        /* ignore */
      }
    }
  }, [auth.isAuthenticated, auth.currentUser?.id, auth.accessToken, centrifuge, isReady, i18n.apiBaseUrl, authHeaders, billing.canUseWorkspace])

  /** WS operator.<me>.settings — JWT server-side; client-level publication only. */
  useEffect(() => {
    if (!auth.isAuthenticated || !centrifuge || !isReady || !auth.currentUser?.id || !i18n.apiBaseUrl || !billing.canUseWorkspace) {
      return
    }

    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    const me = auth.currentUser.id
    const channel = `operator.${me}.settings`

    const onPublication = (ctx: { channel?: string; data?: unknown }) => {
      if (ctx?.channel !== channel) return
      if (!isOperatorSettingsUpdatedEvent(ctx?.data)) return
      void store.operatorWorkspaceSettings.refresh(base, authHeaders)
    }

    centrifuge.on('publication', onPublication)

    return () => {
      try {
        centrifuge.off?.('publication', onPublication)
      } catch {
        /* ignore */
      }
    }
  }, [auth.isAuthenticated, auth.currentUser?.id, auth.accessToken, centrifuge, isReady, i18n.apiBaseUrl, authHeaders, billing.canUseWorkspace])

  /** POST /api/operator/heartbeat — send once, then every 30s. */
  useEffect(() => {
    if (!auth.isAuthenticated || !i18n.apiBaseUrl || !billing.canUseWorkspace) return

    const base = (i18n.apiBaseUrl || '').replace(/\/$/, '')
    heartbeatConfig = {
      baseUrl: base,
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    }
    heartbeatConsumers += 1
    ensureHeartbeatLoop()

    return () => {
      heartbeatConsumers = Math.max(0, heartbeatConsumers - 1)
      stopHeartbeatLoopIfUnused()
    }
  }, [auth.isAuthenticated, auth.accessToken, i18n.apiBaseUrl, authHeaders, billing.canUseWorkspace])

  const refreshConnection = useCallback(async () => {
    const inst = centrifugeRef.current
    const base = (i18n.apiBaseUrl ?? '').replace(/\/$/, '')
    if (!inst || !base || !auth.accessToken) return inst ?? null

    suppressCountersDisconnectBannerRef.current = true
    reinitPendingRef.current = true
    try {
      // Do NOT call setToken / setData on the live client — mutating credentials
      // on a live Centrifuge instance is what caused hybrid `data.ticket` +
      // `connect.token` frames in production. Force a full re-init so the new
      // instance is built strictly for its declared authMode.
      try {
        const state = String((inst as { state?: string }).state ?? '')
        if (state === 'connected' || state === 'connecting') {
          inst.disconnect()
        }
      } catch (err) {
        console.error('[DialogsCountersProvider] refreshConnection disconnect failed:', err)
      }

      const waited = new Promise<any>((resolve) => {
        const timer = window.setTimeout(() => {
          refreshWaitersRef.current = refreshWaitersRef.current.filter((w) => w.timer !== timer)
          resolve(centrifugeRef.current)
        }, 20_000)
        refreshWaitersRef.current.push({ resolve, timer })
      })
      setReinitEpoch((n) => n + 1)
      const next = await waited
      return next
    } finally {
      reinitPendingRef.current = false
      suppressCountersDisconnectBannerRef.current = false
    }
  }, [auth.accessToken, i18n.apiBaseUrl])

  const value = useMemo(
    () => ({
      centrifuge,
      isReady,
      widgetIds,
      workspaceEnabled: billing.canUseWorkspace,
      refreshConnection,
    }),
    [centrifuge, isReady, widgetIds, billing.canUseWorkspace, refreshConnection],
  )

  return (
    <CentrifugeContext.Provider value={value}>
      {children}
    </CentrifugeContext.Provider>
  )
})
